import { ORDENABLE_PAGE_SIZE, REQUEST_STALE_TIME_IN_MS } from '@/constants';
import { useTreeCourses } from '@/data/context/TreeCoursesContext';
import useCourseTreeNavigate from '@/data/hook/course-line/useCourseTreeNavigate';
import usePedWorkspace from '@/data/hook/ped-content/usePedWorkspace';
import useListService from '@/data/hook/useListService';
import { coursePathsQueryKeys } from '@/data/services/querykeys';
import { mountKey } from '@/utils/courses';
import { ROUTES } from '@/utils/routes';
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

const useCoursePathVersions = () => {
  const { coursePath, courseLineId, moduleId } = usePedWorkspace();
  const { navigate } = useCourseTreeNavigate();
  const { tree } = useTreeCourses();
  const courseId = coursePath?.course?.id;

  const versionsQuery = coursePathsQueryKeys.list({
    courseId: courseId ? [courseId] : undefined,
    ordering: '-version',
    pageSize: ORDENABLE_PAGE_SIZE,
  });

  const queryClient = useQueryClient();

  const { results: versions, isLoading } = useListService({
    ...versionsQuery,
    enabled: !!courseId,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const invalidateVersions = useCallback(
    () => queryClient.invalidateQueries({ queryKey: versionsQuery.queryKey }),
    [queryClient, versionsQuery.queryKey],
  );

  const changeVersion = useCallback(
    (slug: string) => {
      if (!slug || slug === coursePath?.slug) return;

      const version = versions.find(version => version.slug === slug);
      const parentKey = mountKey({ type: 'module', id: moduleId });
      if (version && tree?.current.nodes[parentKey]) {
        tree.current.upsert(
          {
            id: mountKey({ type: 'course', id: slug }),
            name: version.course.abbreviation,
            type: 'course',
            realId: slug,
          },
          parentKey,
        );
      }

      navigate(
        ROUTES.ADMIN.COURSE_LINES.PED_CONTENT(courseLineId, moduleId, slug),
      );
    },
    [navigate, courseLineId, moduleId, coursePath?.slug, versions, tree],
  );

  return {
    versions,
    selectedSlug: coursePath?.slug,
    changeVersion,
    isLoading,
    invalidateVersions,
  };
};

export default useCoursePathVersions;
